import {
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { Fragment, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { YESNO } from '@/common/constants/common';
import { useGetDicListQuery } from '@/api/common';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import {
	useEditFamilyMutation,
	useGetAdmissionRequestDetailQuery,
} from '@/api/member-management/admissionRequest';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: string;
}
export default function EditFamilyInformation(props: Props) {
	const { type } = props;
	const memberType = i18n.t(
		type === 'admissionRequest'
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId } = useParams();
	const [editFamily] = useEditFamilyMutation();

	const { data: relationshipDicList } = useGetDicListQuery('relationship', {
		refetchOnMountOrArgChange: true,
	});

	const { data: details } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
			if (details.data.familyCarersList) {
				const arr = details.data.familyCarersList.map((item: any) => {
					return {
						...item,
						emergencyContact: item.emergencyContact ? true : false,
					};
				});
				familyCarersReplace(arr);
			}
		}
	}, [details]);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		familyCarersList: Yup.array().of(
			Yup.object().shape({
				familyCarerName: Yup.string().required(t('errorMessage.pleaseEnter')),
				age: Yup.string().nullable(),
				address: Yup.string().required(t('errorMessage.pleaseEnter')),
				telNo: Yup.string()
					.required(t('errorMessage.pleaseEnter'))
					.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
				relationship: Yup.string().required(t('errorMessage.pleaseSelect')),
				emergencyContact: Yup.string().required(t('errorMessage.pleaseSelect')),
			})
		),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	const {
		fields: familyCarersFields,
		append: familyCarersAppend,
		replace: familyCarersReplace,
		update: familyCarersUpdate,
	}: any = useFieldArray({
		control: methods.control,
		name: 'familyCarersList',
	});

	const addFamilyCarers = () => {
		familyCarersAppend({});
	};

	// Removing a row shifts the field name of every row below it. The inputs are
	// controllers bound to those names, so with remove() they keep the value they
	// held under the old index and the last row's values drop out of the form
	// state - the row still shows them, but submitting reports it as empty.
	// Rebuilding the list with replace() hands every row a fresh key, so they
	// remount against the shifted values.
	const removeFamilyCarers = (index: number) => {
		familyCarersReplace(
			(methods.getValues('familyCarersList') || []).filter(
				(_: any, i: number) => i !== index
			)
		);
	};
	const onSubmit: SubmitHandler<any> = async (data) => {
		const obj: any = { ...data };
		const response = await editFamily(obj);

		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				`/member-management/${
					type == 'member' ? 'member-list' : 'admission-request-list'
				}/detail/${admissionRequestId}`
			);
		}
	};

	const handleClose = () => {
		navigate(
			`/member-management/${
				type == 'member' ? 'member-list' : 'admission-request-list'
			}/detail/${admissionRequestId}`,
			{ replace: true }
		);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h4'>
								{type == 'admissionRequest'
									? t('drawerListItems.editAdmissionRequest')
									: t('drawerListItems.editMember')}
							</Typography>
						</Box>
						<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
							<Box
								display='flex'
								justifyContent='space-between'
								marginBottom={2}
							>
								<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
									{t('member.familyCarersInformation')}
								</Typography>
							</Box>
							{familyCarersFields.map((item: any, index: number) => (
								<Fragment key={item.id}>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
										}}
										marginBottom={1}
									>
										<Typography variant='body1'>
											{t('preAdmission.familyCarer') + ` ${index + 1}`}
										</Typography>
										<IconButton
											onClick={() => {
												removeFamilyCarers(index);
											}}
										>
											<CloseOutlinedIcon />
										</IconButton>
									</Box>

									<Grid container spacing={3} marginTop={0} marginBottom={2}>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name={`familyCarersList[${index}].familyCarerName`}
												label={t('preAdmission.name') + '*'}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name={`familyCarersList[${index}].age`}
												label={t('preAdmission.age')}
											/>
										</Grid>
										<Grid size={6}>
											<AppAutoCompleteMenu
												control={methods.control}
												name={`familyCarersList[${index}].relationship`}
												label={t('preAdmission.relationship') + '*'}
												options={(relationshipDicList?.data || []).map(
													(item: { labelEn: any }) => {
														return {
															...item,
															name: item.labelEn,
														};
													}
												)}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name={`familyCarersList[${index}].address`}
												label={t('preAdmission.address') + '*'}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												control={methods.control}
												name={`familyCarersList[${index}].telNo`}
												label={t('preAdmission.telNo') + '*'}
											/>
										</Grid>
										<Grid size={6}>
											<Typography variant='body2' color='common.neutral40'>
												{t('preAdmission.emergencyContact') + '*'}
											</Typography>
											<AppRadioGroup
												control={methods.control}
												name={`familyCarersList[${index}].emergencyContact`}
												row
												options={YESNO}
											/>
										</Grid>
									</Grid>
								</Fragment>
							))}
							{familyCarersFields.length < 30 && (
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={12}>
										<Button
											startIcon={<AddCircleOutlineOutlinedIcon />}
											size='large'
											onClick={addFamilyCarers}
										>
											{t('button.newFamilyCarer')}
										</Button>
									</Grid>
								</Grid>
							)}
							<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
								<Button onClick={handleClose}>{t('button.cancel')}</Button>
								<Button
									type='submit'
									variant='contained'
									endIcon={<CheckCircleOutlineIcon />}
								>
									{t('button.complete')}
								</Button>
							</CardActions>
						</Card>
					</form>
				</FormProvider>
			</Box>
		</>
	);
}
